import {
  IconCalendar,
  IconCalendarFilled,
  IconHome,
  IconHomeFilled,
  IconLibrary,
  IconMessage,
  IconMessageFilled,
  IconUser,
  IconUserFilled,
  IconUsers,
} from "@tabler/icons-react";
import { Route, Switch } from "wouter";
import { ClientDetail, ClientsList } from "@/pages/trainer/Clients";
import { FilePicker, FilePreviewForChat } from "@/pages/trainer/FilePicker";
import { FileUpload } from "@/pages/trainer/FileUpload";
import { GroupDetail, Groups } from "@/pages/trainer/Groups";
import { JoinGroup } from "@/pages/trainer/JoinGroup";
import { FileEdit, FolderEdit, Library } from "@/pages/trainer/Library";
import { MoveToFolder } from "@/pages/trainer/MoveToFolder";
import { NewSurvey } from "@/pages/trainer/NewSurvey";
import { BookingSettings } from "@/pages/trainer/profile/BookingSettings";
import { Gallery } from "@/pages/trainer/profile/Gallery";
import {
  GroupClassTypeEdit,
  GroupClassTypes,
} from "@/pages/trainer/profile/GroupClassTypes";
import { NewProfile } from "@/pages/trainer/profile/NewProfile";
import { PersonalData } from "@/pages/trainer/profile/PersonalData";
import { PortraitUpload } from "@/pages/trainer/profile/Portait";
import { PublicProfile } from "@/pages/trainer/profile/PublicProfile";
import { ScheduleEdit } from "@/pages/trainer/profile/ScheduleEdit";
import { Schedules } from "@/pages/trainer/profile/Schedules";
import {
  SessionTypeEdit,
  SessionTypes,
} from "@/pages/trainer/profile/SessionTypes";
import { Session } from "@/pages/trainer/Session";
import { SessionList } from "@/pages/trainer/SessonList";
import { SurveyResult } from "@/pages/trainer/SurveyResult";
import { NewTextNote } from "@/pages/trainer/TextNote";
import { Chat } from "@/pages/universal/Chat";
import { ChatsWithTabs } from "@/pages/universal/ChatsWithTabs";
import { FilePreview } from "@/pages/universal/FilePreview";
import { GroupChat } from "@/pages/universal/GroupChat";
import { NotFound } from "@/pages/universal/NotFound";
import { NotificationSettings } from "@/pages/universal/NotificationSettings";
import { Notifications } from "@/pages/universal/Notifications";
import { TrainerPage } from "@/pages/universal/TrainerPage";
import { type NavItem, TrainerRoutes, UniversalRoutes } from "@/routers/routes";
import { AccountSettings } from "../pages/trainer/AccountSettings";
import { Home, InviteClients } from "../pages/trainer/Home";

export const TrainerNavigationIcons = [
  {
    icon: IconHome,
    selectedIcon: IconHomeFilled,
    label: "Home",
    route: UniversalRoutes.HOME,
  },
  {
    icon: IconLibrary,
    selectedIcon: IconLibrary,
    label: "Library",
    route: TrainerRoutes.LIBRARY.LIST,
  },
  {
    icon: IconMessage,
    selectedIcon: IconMessageFilled,
    label: "Messages",
    route: [UniversalRoutes.CHAT.LIST, UniversalRoutes.GROUP_CHAT.DETAIL.base],
    chat: true as const,
  },
  {
    icon: IconCalendar,
    selectedIcon: IconCalendarFilled,
    label: "Sessions",
    route: [UniversalRoutes.SESSIONS.LIST, TrainerRoutes.CLIENTS.LIST],
  },
  {
    icon: IconUser,
    selectedIcon: IconUserFilled,
    label: "Profile",
    route: UniversalRoutes.PROFILE.HOME,
  },
] as const satisfies NavItem[];

export const TrainerNavigationIconsDesktop = [
  {
    icon: IconHome,
    selectedIcon: IconHomeFilled,
    label: "Home",
    route: UniversalRoutes.HOME,
  },
  {
    icon: IconLibrary,
    selectedIcon: IconLibrary,
    label: "Library",
    route: TrainerRoutes.LIBRARY.LIST,
  },
  {
    icon: IconMessage,
    selectedIcon: IconMessageFilled,
    label: "Messages",
    route: [UniversalRoutes.CHAT.LIST, UniversalRoutes.GROUP_CHAT.DETAIL.base],
    chat: true as const,
  },
  {
    icon: IconCalendar,
    selectedIcon: IconCalendarFilled,
    label: "Clients",
    route: [TrainerRoutes.CLIENTS.LIST],
  },
  {
    icon: IconCalendar,
    selectedIcon: IconCalendarFilled,
    label: "Sessions",
    route: [UniversalRoutes.SESSIONS.LIST],
  },
  {
    icon: IconUsers,
    selectedIcon: IconUsers,
    label: "Groups",
    route: [UniversalRoutes.GROUPS.LIST],
  },
] as const satisfies NavItem[];

export function TrainerRouter() {
  return (
    <Switch>
      <Route path={UniversalRoutes.HOME}>
        <Home />
      </Route>
      <Route path={TrainerRoutes.INVITE}>
        <InviteClients />
      </Route>
      <Route path={UniversalRoutes.PROFILE.CREATE}>
        <NewProfile />
      </Route>
      <Route path={UniversalRoutes.PROFILE.HOME}>
        <AccountSettings />
      </Route>
      <Route path={UniversalRoutes.PROFILE.PERSONAL_INFO}>
        <PersonalData />
      </Route>
      <Route path={UniversalRoutes.PROFILE.PORTRAIT}>
        <PortraitUpload />
      </Route>
      <Route path={TrainerRoutes.PROFILE.GALLERY}>
        <Gallery />
      </Route>
      <Route path={TrainerRoutes.PROFILE.SCHEDULES}>
        <Schedules />
      </Route>
      <Route path={TrainerRoutes.PROFILE.SCHEDULE_EDIT.path}>
        <ScheduleEdit />
      </Route>
      <Route path={TrainerRoutes.PROFILE.SESSION_TYPES}>
        <SessionTypes />
      </Route>
      <Route path={TrainerRoutes.PROFILE.SESSION_TYPE_EDIT.path}>
        <SessionTypeEdit />
      </Route>
      <Route path={TrainerRoutes.PROFILE.GROUP_CLASS_TYPES}>
        <GroupClassTypes />
      </Route>
      <Route path={TrainerRoutes.PROFILE.GROUP_CLASS_TYPE_EDIT.path}>
        <GroupClassTypeEdit />
      </Route>
      <Route path={TrainerRoutes.PROFILE.BOOKING_SETTINGS}>
        <BookingSettings />
      </Route>
      <Route path={TrainerRoutes.PROFILE.PUBLIC_PROFILE}>
        <PublicProfile />
      </Route>
      <Route path={UniversalRoutes.PROFILE.NOTIFICATIONS}>
        <NotificationSettings />
      </Route>
      <Route path={UniversalRoutes.SESSIONS.DETAIL.path}>
        <Session />
      </Route>
      <Route path={UniversalRoutes.SESSIONS.LIST}>
        <SessionList />
      </Route>
      <Route path={TrainerRoutes.LIBRARY.LIST}>
        <Library />
      </Route>
      <Route path={TrainerRoutes.LIBRARY.FOLDER.path}>
        <Library />
      </Route>
      <Route path={TrainerRoutes.LIBRARY.UPLOAD.path}>
        <FileUpload />
      </Route>
      <Route path={TrainerRoutes.LIBRARY.NEW_TEXT_NOTE.path}>
        <NewTextNote />
      </Route>
      <Route path={TrainerRoutes.LIBRARY.NEW_SURVEY.path}>
        <NewSurvey />
      </Route>
      <Route path={TrainerRoutes.LIBRARY.FILE_EDIT.path}>
        <FileEdit />
      </Route>
      <Route path={TrainerRoutes.LIBRARY.FOLDER_EDIT.path}>
        <FolderEdit />
      </Route>
      <Route path={TrainerRoutes.LIBRARY.MOVE_TO_FOLDER.path}>
        <MoveToFolder />
      </Route>
      <Route path={TrainerRoutes.CLIENTS.LIST}>
        <ClientsList />
      </Route>
      <Route path={TrainerRoutes.CLIENTS.DETAIL.path}>
        <ClientDetail />
      </Route>
      <Route path={UniversalRoutes.GROUPS.LIST}>
        <Groups />
      </Route>
      <Route path={UniversalRoutes.GROUPS.DETAIL.path}>
        <GroupDetail />
      </Route>
      <Route path={TrainerRoutes.JOIN_GROUP.path}>
        <JoinGroup />
      </Route>
      <Route path={UniversalRoutes.CHAT.LIST}>
        <ChatsWithTabs whoAmI="trainer" />
      </Route>
      <Route path={UniversalRoutes.CHAT.DETAIL.path}>
        <Chat who="trainer" />
      </Route>
      <Route path={UniversalRoutes.GROUP_CHAT.DETAIL.path}>
        <GroupChat />
      </Route>
      <Route path={TrainerRoutes.CHAT.FILE_PICKER.path}>
        <FilePicker />
      </Route>
      <Route path={TrainerRoutes.CHAT.FILE_PREVIEW.path}>
        <FilePreviewForChat />
      </Route>
      <Route path={UniversalRoutes.NOTIFICATIONS.LIST}>
        <Notifications />
      </Route>
      <Route path={TrainerRoutes.SURVEYS.RESULTS.path}>
        <SurveyResult />
      </Route>
      <Route path={UniversalRoutes.FILE_PREVIEW.path}>
        <FilePreview />
      </Route>
      <Route path={UniversalRoutes.TRAINER.path}>
        <TrainerPage />
      </Route>
      <Route path={UniversalRoutes.TRAINERS.DETAIL.path}>
        <TrainerPage />
      </Route>
      <Route>
        <NotFound />
      </Route>
    </Switch>
  );
}
